import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PanelLeftOpen } from 'lucide-react';
import { cn } from '@/lib/utils';

import { useAudio, useAuth, useConfig } from '@chainlit/react-client';

import AudioPresence from '@/components/AudioPresence';
import ButtonLink from '@/components/ButtonLink';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/ui/sidebar';

import ApiKeys from './ApiKeys';
import ChatProfiles from './ChatProfiles';
import NewChatButton from './NewChat';
import SidebarTrigger from './SidebarTrigger';
import { ThemeToggle } from './ThemeToggle';
import UserNav from './UserNav';

interface HeaderProps {
  onMobileSidebarToggle?: () => void;
  hasLeftSidebar?: boolean;
  onSidebarRestore?: () => void;
}

const Header = memo(({ onMobileSidebarToggle, hasLeftSidebar = false, onSidebarRestore }: HeaderProps) => {
  const { audioConnection } = useAudio();
  const navigate = useNavigate();
  const { data } = useAuth();
  const { config } = useConfig();
  const { open, openMobile, isMobile } = useSidebar();

  const sidebarOpen = isMobile ? openMobile : open;

  const historyEnabled = data?.requireLogin && config?.dataPersistence;

  const links = config?.ui?.header_links || [];

  // Show NewChat button only when:
  // 1. Not on mobile AND no left sidebar is visible (collapsed or not available)
  // 2. History is not enabled OR (history is enabled but mobile sidebar is closed)
  const showNewChatButton = !isMobile && !hasLeftSidebar && !onSidebarRestore && (!historyEnabled || (historyEnabled && !sidebarOpen));

  return (
    <div
      className={cn(
        "flex h-[60px] items-center justify-between gap-2 relative",
        onSidebarRestore ? "pl-2 pr-3 py-3" : "p-3"
      )}
      id="header"
    >
      <div className="flex items-center">
        {/* Show sidebar restore button when sidebar is collapsed - positioned at far left */}
        {onSidebarRestore ? (
          <Button
            onClick={onSidebarRestore}
            size="sm"
            variant="ghost"
            className="h-8 w-8 mr-2"
            title="Show Sidebar"
          >
            <PanelLeftOpen className="!size-5" />
          </Button>
        ) : (
          <>
            {historyEnabled ? !sidebarOpen ? <SidebarTrigger onMobileToggle={onMobileSidebarToggle} /> : null : null}
            {showNewChatButton && <NewChatButton navigate={navigate} />}
          </>
        )}

        <ChatProfiles navigate={navigate} />
      </div>

      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        {audioConnection === 'on' ? (
          <AudioPresence
            type="server"
            height={35}
            width={70}
            barCount={4}
            barSpacing={2}
          />
        ) : null}
      </div>

      <div />
      <div className="flex items-center gap-1">
        <ApiKeys />
        {links &&
          links.map((link, index) => (
            <ButtonLink
              key={`${link.name}-${link.url}-${index}`}
              name={link.name}
              displayName={link.display_name}
              iconUrl={link.icon_url}
              url={link.url}
            />
          ))}
        <ThemeToggle />
        <UserNav />
      </div>
    </div>
  );
});

export { Header };
